import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Machine } from "../GameComponents/Machine";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { PlayList } from "../Components/Sounds/PlayList";
import { AppContext } from "../../Common/AppContext";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { Symbol } from "../GameComponents/Symbol";
import { ReelExtended } from "./ReelExtended";
import { Configuration } from "../Components/Configuration";
import { Reel } from "../GameComponents/Reel";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";

@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private static readonly AdditionalStopDelay: number = 20;

    private static readonly AdditionalMysteryStopDelay: number = 20;

    static Respin: boolean = false;

    //emit machineStopped with delay
    private _machineStopTimer: FrameTimerEventEmitter;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this._machineStopTimer = CreateFrameTimer("MachineStoppedDelay");
        this._machineStopTimer.on(FrameTimerEvents.Complete, this.onMachineStoppedDelayed.bind(this));
    }

    public initializeRespinReels(respinReels: Array<Array<number>>): void {
        for (let i = 0; i < this._reels.length; i++) {
            this._reels[i].setRespinStrip(respinReels[i]);
        }
    }

    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
    ): void {
        MachineExtended.Respin = false;

        if (DEBUG) {
            this.log("spinStart");
        }

        this.visible = true;
        this._reelsContainer.visible = true;

        this.actualReelStopped = -1;
        this._stoppedReelIndexes = 0;

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;

        this._startSpinTime = Date.now();

        if (this._reels != null) {
            for (let i = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    if (this._stoppedSymbolsArray.length > 0) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(this._stoppedSymbolsArray[i]);

                        //move current symbols from WinAnimator to Reel, not array reference
                        this._reels[i].currentSymbols = [];
                        for (let j = 0; j < this._visibleSymbolCount; j++) {
                            this._reels[i].currentSymbols.push(currentSymbols[i][j]);
                            currentSymbols[i][j] = null;
                        }

                        this._reels[i].showSymbols(useTransform);
                        this._reels[i].spinStart();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no stopped symbols");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reel with index: " + i);
                    }
                }
            }

            if (longRotation != null && longRotation) {
                this._stopTimer.start(this._minSpinRotationLong);
            } else {
                this._stopTimer.start(this._minSpinRotation);
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }

        PlayList.play("SND_BaseRotationStart");
        PlayList.loop("SND_BaseRotation", 0, true);
    }

    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (DEBUG) {
            this.log("spinStop " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (!MachineExtended.Respin && !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);
                            this._reels[i].spinStop(force);
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    public onReelStopped(reelIndex: number): void {
        PlayList.play("SND_BaseRotationEnd" + reelIndex);

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        this._reels[reelIndex].visible = false;
        this.emit(Machine.ReelStopped);

        if (this._stoppedReelIndexes === this._reelCount) {
            if (this._stoppedSymbolsArray[reelIndex].transform != null) {
                this._machineStopTimer.start(MachineExtended.AdditionalStopDelay);
            } else if (this._stoppedSymbolsArray[reelIndex].overlay != null) {
                this._machineStopTimer.start(MachineExtended.AdditionalMysteryStopDelay);
            } else {
                this.onMachineStoppedDelayed();
            }
        }
    }

    private onMachineStoppedDelayed(): void {
        this._machineStopTimer.stop();

        PlayList.stop("SND_BaseRotation", 10);
        if (this._legalStopTimer != null) {
            clearTimeout(this._legalStopTimer);
        }
        this._stopTimer.stop();

        if (DEBUG) {
            const old = AppContext.gameConfiguration.MinGameRoundDuration;
            const current = AppContext.gameConfiguration.GameRoundMinSpan;
            const minDuration = current > 0 ? current : old > 0 ? old : 0;
            const spinDuration = Date.now() - this._startSpinTime;

            if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                this.log("Spin time duration: " + spinDuration + " ms");
            } else {
                console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
            }
        }

        this.emit(Machine.MachineStopped);
        this._reelsContainer.visible = false;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/